import './McpConnectorSection.scss';
import { Icon, toast } from '@components';
import React, { useState } from 'react';
import { Accordion, Card, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';

// Thinaer-only; unlisted hosts hide the section.
const THINAER_API_HOSTS = [
  'odata-api.dev.thinaer.io',
  'odata-api.test-sql.thinaer.io',
  'odata-api.prod-sql.thinaer.io',
];

// REACT_APP_MCP_URL if set, else derived from the API host.
const MCP_URL: string = (() => {
  try {
    const host = new URL(window._sonar_env?.REACT_APP_BASE_API_URL_V2 || '').host;
    if (!THINAER_API_HOSTS.includes(host)) return '';
    return (
      window._sonar_env?.REACT_APP_MCP_URL || `https://${host.replace(/^odata-api\./, 'mcp.')}/mcp`
    );
  } catch {
    return '';
  }
})();

const CONNECT_GUIDES: { id: string; label: string; steps: string[] }[] = [
  {
    id: 'claude',
    label: 'Claude',
    steps: [
      'In Claude, open Settings → Connectors (available on paid plans) and click “Add custom connector.”',
      'Name it “Sonar” and paste the MCP URL above.',
      'Click Connect. When the Sonar window opens, paste your API Access Token above.',
      'Start a new chat — your Sonar tools appear there.',
    ],
  },
  {
    id: 'chatgpt',
    label: 'ChatGPT',
    steps: [
      'In ChatGPT, open Settings → Connectors (available on paid plans).',
      'Add a custom connector and paste the MCP URL above.',
      'When prompted, paste your API Access Token to authorize.',
      'Start a new chat and turn on the Sonar connector.',
    ],
  },
  {
    id: 'other',
    label: 'Other',
    steps: [
      'In any MCP-compatible app, add a custom or remote MCP server.',
      'Use the MCP URL above as the server address.',
      'When it asks you to authorize, paste your API Access Token.',
      'Your Sonar tools appear in a new chat or session.',
    ],
  },
];

const getJwtFromStorage = (): string => {
  const persistRootRaw = localStorage.getItem('persist:root');
  if (!persistRootRaw) return '';
  try {
    const persistRoot = JSON.parse(persistRootRaw);
    const authRaw = persistRoot?.auth;
    if (!authRaw) return '';
    const auth = JSON.parse(authRaw);
    return (auth?.token || '').replace(/^Bearer\s+/i, '').trim();
  } catch {
    return '';
  }
};

interface Props {
  eventKey: string;
  onSelect?: (key: number) => void;
}

const McpConnectorSection: React.FC<Props> = ({ eventKey, onSelect }) => {
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [token, setToken] = useState('');
  const [guideId, setGuideId] = useState('claude');
  const guide = CONNECT_GUIDES.find((g) => g.id === guideId) || CONNECT_GUIDES[0];

  const revealToken = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!tokenRevealed) {
      const jwt = getJwtFromStorage();
      if (!jwt) {
        toast.error('Token not found');
        return;
      }
      setToken(jwt);
    }
    setTokenRevealed((prev) => !prev);
  };

  const copyValue = async (value: string, label: string, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch {
      toast.error('Copy failed.');
    }
  };

  const copyToken = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = token || getJwtFromStorage();
    if (!value) {
      toast.error('Token not found');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Token copied.');
    } catch {
      toast.error('Copy failed.');
    }
  };

  if (!MCP_URL) return null;

  return (
    <Accordion.Item eventKey={eventKey} onClick={() => onSelect?.(Number(eventKey))}>
      <Accordion.Header>
        <span className="mcp-header">
          AI Connector <span className="mcp-beta">Beta</span>
        </span>
      </Accordion.Header>
      <Accordion.Body>
        <Card.Body className="mcp-connector-body">
          <p className="mb-3">
            Connect Sonar to Claude, ChatGPT, and other AI assistants so they can answer questions
            about your data in plain English. Read-only — the connector can’t change or delete
            anything in Sonar.
          </p>

          <Form.Group>
            <Form.Label className="user-info-labels">MCP URL</Form.Label>
            <InputGroup>
              <Form.Control value={MCP_URL} disabled readOnly aria-label="MCP URL" />
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-copy-url">Copy MCP URL to Clipboard</Tooltip>}
              >
                <span
                  className="d-inline-flex fa-copy-wrapper"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => copyValue(MCP_URL, 'MCP URL', e)}
                >
                  <i className="far fa-lg fa-copy col-1"></i>
                </span>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <Form.Group>
            <Form.Label className="user-info-labels pt-2">API Access Token</Form.Label>
            <InputGroup>
              <Form.Control
                value={tokenRevealed ? token : '••••••••••••••••••••••••••••'}
                disabled
                readOnly
                aria-label="API Access Token"
              />
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-show-token">Show/Hide API Access Token</Tooltip>}
              >
                <span className="d-inline-flex" onClick={revealToken} role="button" tabIndex={0}>
                  <Icon
                    name={tokenRevealed ? 'view' : 'hide'}
                    variant="dark"
                    size="sm"
                    disabled={false}
                    showPointer
                  />
                </span>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-copy-token">Copy API Access Token to Clipboard</Tooltip>
                }
              >
                <span
                  className="d-inline-flex fa-copy-wrapper"
                  role="button"
                  tabIndex={0}
                  onClick={copyToken}
                >
                  <i className="far fa-lg fa-copy col-1"></i>
                </span>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <Form.Group>
            <Form.Label className="user-info-labels pt-2">How to connect</Form.Label>
            <Form.Select
              value={guideId}
              onChange={(e) => setGuideId(e.target.value)}
              aria-label="Choose an AI app"
            >
              {CONNECT_GUIDES.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </Form.Select>
            <ol className="ps-3 mb-0 pt-3">
              {guide.steps.map((step) => (
                <li key={step} className="pb-2">
                  {step}
                </li>
              ))}
            </ol>
          </Form.Group>
        </Card.Body>
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default McpConnectorSection;
